import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Bootstrap components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation"; // Import navigation styles

// Axios
import axios from "axios";

// Custom Styles
import "../scss/pages/home.styles.scss";

// Assets
import ImageHeroBg from "../assets/img/image-hero-home.png";
import CertificateIcon from "../assets/img/certificate-icon--white.svg";
import AcademicIcon from "../assets/img/acedemic-icon--white.svg";
import AboutUsHomeInstitution1 from "../assets/img/about-us-home-institution-1.jpg";
import AboutUsHomeInstitution2 from "../assets/img/about-us-home-institution-2.jpg";
import ImageVideoPlacehoder from "../assets/img/video-placeholder-home.jpg";
import WhyUsPerkNumber1 from "../assets/img/perk-01.svg";
import WhyUsPerkNumber2 from "../assets/img/perk-02.svg";
import WhyUsPerkNumber3 from "../assets/img/perk-03.svg";
import CourseImageHeader from "../assets/img/course-image-header.jpg";

import CustomPrevIcon from "../assets/img/left-arrow--white.svg";
import CustomNextIcon from "../assets/img/right-arrow--white.svg";

import {
  getYouTubeEmbedUrl,
  getYouTubeVideoId,
} from "../utils/youtube";
import {
  filterLicenciaturasWithAttributes,
  getLicenciaturaImageUrl,
} from "../utils/licenciaturaImage";

function getUpcomingEventsFromInicio(eventosInicio) {
  if (!Array.isArray(eventosInicio)) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return eventosInicio
    .map((item) => item?.eventos?.data)
    .filter(Boolean)
    .filter((event) => {
      const fecha = event.attributes?.Fecha;
      if (!fecha) return true;
      const eventDate = new Date(fecha);
      return !Number.isNaN(eventDate.getTime()) && eventDate >= today;
    });
}

function Home() {
  const [homeData, setHomeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({ error: false, message: "" });
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Main home data query (without deep nested Licenciaturas)
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/graphql`,
          {
            query: `
          {
            inicio {
              data {
                attributes {
                  Header {
                    Subtitulo
                    Titulo
                    EstadisticasHeader {
                      Numero
                      Titulo
                    }
                    ImagenHeader {
                      data {
                        attributes {
                          url
                        }
                      }
                    }
                  }
                  AcercaDe {
                    Imagen1 {
                      data {
                        attributes {
                          url
                        }
                      }
                    }
                    Imagen2 {
                      data {
                        attributes {
                          url
                        }
                      }
                    }
                    Titulo
                    Parrafo
                  }
                  MejorOpcion {
                    Titulo
                    Parrafo
                    Beneficios
                    VideoYoutube
                  }
                  ParrafoLicenciaturas
                  EventosInicio {
                    eventos {
                      data {
                        id
                        attributes {
                          Nombre
                          Descripcion
                          Fecha
                        }
                      }
                    }
                  }
                  ImagenContacto {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        `,
          }
        );

        // Fetch licenciaturas separately (you already have this working)
        const licenciaturasResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/graphql`,
          {
            query: `
          {
            licenciaturas {
              data {
                id
                attributes {
                  Nombre
                  Descripcion
                  ImagenLicenciatura {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        `,
          }
        );

        const homeData = response.data.data?.inicio?.data?.attributes;
        if (!homeData) {
          throw new Error("Inicio data unavailable");
        }
        const licenciaturasList = filterLicenciaturasWithAttributes(
          licenciaturasResponse.data.data?.licenciaturas?.data
        );
        homeData.Licenciaturas = {
          licenciaturas: { data: licenciaturasList },
        };

        setHomeData(homeData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError({ error: true, message: error.message || error.toString() });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <section className="loading">
        <Container>
          <h6>Cargando información...</h6>
        </Container>
      </section>
    );
  }
  if (error.error) {
    return (
      <section className="loading">
        <Container>
          <h6>No se pudo cargar la información en este momento ⌛️</h6>
        </Container>
      </section>
    );
  }
  const {
    Header,
    AcercaDe,
    MejorOpcion,
    ParrafoLicenciaturas,
    Licenciaturas: { licenciaturas: { data: Licenciaturas } },
    EventosInicio,
    ImagenContacto,
  } = homeData || {};

  const upcomingEvents = getUpcomingEventsFromInicio(EventosInicio);

  if (
    !Header ||
    !AcercaDe ||
    !MejorOpcion ||
    !ParrafoLicenciaturas ||
    !Licenciaturas ||
    !EventosInicio ||
    !ImagenContacto
  ) {
    return (
      <section className="loading">
        <Container>
          <h6>No hay datos disponibles en este momento ⌛️</h6>
        </Container>
      </section>
    );
  }

  const rawItems = (MejorOpcion?.Beneficios || "")
    ?.split(/\n\d+\.\s/)
    .filter(Boolean);

  // Process each item to separate into heading and paragraph
  const whyUsVideoId = getYouTubeVideoId(MejorOpcion?.VideoYoutube);
  const whyUsEmbedUrl = getYouTubeEmbedUrl(whyUsVideoId);

  const listItems = rawItems.map((item, index) => {
    // Extract the number at the beginning of the item
    const headingAndParagraph = item.replace(/^(\d+)\.\s/, "").split("\n");
    const heading = headingAndParagraph[0].trim();
    const paragraph = headingAndParagraph.slice(1).join("\n").trim();

    return {
      heading,
      paragraph,
    };
  });

  // if (!loading && Object.keys(MejorOpcion).length > 0) {
  // console.log(listItems);
  // }
  return (
    <>
      <section className="jumbotron--home">
        <Container fluid>
          <Row>
            <Col className="jumbotron--home__content">
              <div className="jumbotron--home__content__heading">
                <h6>{Header.Subtitulo}</h6>
                <h3>{Header.Titulo}</h3>
              </div>
              <div className="jumbotron--home__perks">
                {Header.EstadisticasHeader.map((stat, key) => (
                  <div key={key} className="jumbotron--home__perk">
                    {stat.Numero !== null ? (
                      <h4>{stat.Numero}</h4>
                    ) : (
                      <img
                        src={CertificateIcon}
                        alt="Escuela certificadora en idioma inglés"
                      />
                    )}
                    <h3>{stat.Titulo}</h3>
                  </div>
                ))}

                {/* <div className="jumbotron--home__perk">
                  
                  <h3>Escuela certificadora en idioma inglés</h3>
                </div>
                <div className="jumbotron--home__perk">
                  <img
                    src={AcademicIcon}
                    alt="Escuela certificadora en idioma inglés"
                  />
                  <h3>Movilidad académica y educación en línea</h3>
                </div> */}
              </div>
            </Col>
            <Col className="jumbotron--home__media">
              <img src={`${Header.ImagenHeader.data.attributes.url}`} alt />
            </Col>
          </Row>
        </Container>
      </section>
      <section className="about--home">
        <Container fluid>
          <h5>Acerca de</h5>
          <Row>
            <Col xs="12" md="7" className="about--home__media">
              <div>
                <img
                  src={`${AcercaDe.Imagen1.data.attributes.url}`}
                  // src={`${process.env.REACT_APP_API_URL}${AcercaDe.Imagen1.data.attributes.url}`}
                  className="img-fluid"
                  alt=""
                />
              </div>
              <div>
                <img
                  src={`${AcercaDe.Imagen2.data.attributes.url}`}
                  // // src={`${process.env.REACT_APP_API_URL}${AcercaDe.Imagen2.data.attributes.url}`}
                  // className="img-fluid"
                  alt=""
                />
                <Link to="/acerca-de" className="about--home__link">
                  Saber más
                </Link>
              </div>
            </Col>
            <Col xs="12" md="5" className="about--home__content">
              <h3>{AcercaDe.Titulo}</h3>
              <p>{AcercaDe.Parrafo}</p>
            </Col>
          </Row>
        </Container>
      </section>
      <section className="about--why-us">
        <Container fluid>
          <Row>
            <Col xs="12" lg="6" xxl="5" className="about--why-us__content">
              <h3>{MejorOpcion.Titulo}</h3>
              <p>{MejorOpcion.Parrafo}</p>
              <div className="about--why-us__content__perks">
                {/* {console.log(
                  MejorOpcion.Beneficios.split(/\n\d+\.\s/).filter(Boolean)
                )} */}
                {listItems.map((listItem, key) => (
                  <div key={key} className="about--why-us__content__perk">
                    {/* <img src={`${WhyUsPerkNumber1}`} alt="" /> */}
                    <h4>0{key + 1}.</h4>
                    <div>
                      <h5>{listItem.heading}</h5>
                      <p>{listItem.paragraph}</p>
                    </div>
                  </div>
                ))}
                {/* <div className="about--why-us__content__perk">
                  <img src={WhyUsPerkNumber2} alt="" />
                  <div>
                    <h5>
                      To take a trivial example, which of us ever undertakes.
                    </h5>
                    <p>
                      Nor again is there anyone who loves or pursues or desires
                      to obtain pain of itself, because it is pain, but because
                      occasionally circumstances.
                    </p>
                  </div>
                </div>
                <div className="about--why-us__content__perk">
                  <img src={WhyUsPerkNumber3} alt="" />
                  <div>
                    <h5>
                      To take a trivial example, which of us ever undertakes.
                    </h5>
                    <p>
                      Nor again is there anyone who loves or pursues or desires
                      to obtain pain of itself, because it is pain, but because
                      occasionally circumstances.
                    </p>
                  </div>
                </div> */}
              </div>
            </Col>
            <Col xs="12" lg="6" xxl="7" className="about--why-us__media">
              <div className="about--why-us__video">
                {whyUsEmbedUrl ? (
                  <iframe
                    src={whyUsEmbedUrl}
                    title={MejorOpcion.Titulo || "Video institucional"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  <img
                    src={ImageVideoPlacehoder}
                    className="img-fluid"
                    alt=""
                  />
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section className="courses">
        <Container fluid>
          <div className="spacer--red"></div>
          <h4>Oferta Educativa</h4>
          <p>{ParrafoLicenciaturas}</p>
          {Licenciaturas.length > 0 ? (
            <>
              <div className="courses__list">
                {Licenciaturas.map((item) => (
                  <div className="course" key={item.id}>
                    <div className="course__media">
                      <img src={getLicenciaturaImageUrl(item)} alt="" />
                    </div>
                    <div className="course__body">
                      <h5>{item.attributes.Nombre}</h5>
                      <p>{item.attributes.Descripcion}</p>
                      <Link to={`/oferta-educativa/${item.id}`}>
                        Más información
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <Link className="courses-link-to-all" to="/oferta-educativa">
                Ver todos
              </Link>
            </>
          ) : (
            <p className="text-center text-muted mt-3">
              No hay licenciaturas disponibles en este momento
            </p>
          )}
        </Container>
      </section>
      <section className="upcoming-events">
        <Container fluid>
          <h4>Próximos eventos</h4>
          <div className="upcoming-events__c">
            {upcomingEvents.length > 0 ? (
              <>
                <Swiper
                  modules={[Navigation]}
                  spaceBetween={30}
                  slidesPerView={1}
                  breakpoints={{
                    640: {
                      slidesPerView: 1,
                      spaceBetween: 20,
                    },
                    768: {
                      slidesPerView: 2,
                      spaceBetween: 30,
                    },
                    1024: {
                      slidesPerView: 3,
                      spaceBetween: 40,
                    },
                  }}
                  navigation={{
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                  }}
                  pagination={{ clickable: true }}
                >
                  {upcomingEvents.map((event) => {
                    const rawFecha = event.attributes.Fecha;
                    const eventDate = rawFecha ? new Date(rawFecha) : null;
                    const hasValidDate =
                      eventDate && !Number.isNaN(eventDate.getTime());

                    return (
                    <SwiperSlide key={event.id}>
                      <div className="upcoming-event">
                        <div className="upcoming-event__heading">
                          <h5>
                            {hasValidDate
                              ? eventDate.toLocaleString("es-MX", {
                                  month: "short",
                                  day: "numeric",
                                })
                              : "Próximamente"}
                          </h5>
                          {hasValidDate ? (
                            <p>
                              {eventDate.toLocaleString("es-MX", {
                                hour: "numeric",
                                hour12: true,
                              })}
                            </p>
                          ) : null}
                        </div>
                        <div className="upcoming-event__body">
                          <h6>{event.attributes.Nombre}</h6>
                          <p>{event.attributes.Descripcion}</p>
                        </div>
                      </div>
                    </SwiperSlide>
                    );
                  })}
                </Swiper>
                <div className="swiper-button-prev">
                  <img src={CustomPrevIcon} alt="Previous" />
                </div>
                <div className="swiper-button-next">
                  <img src={CustomNextIcon} alt="Next" />
                </div>
              </>
            ) : (
              <p className="upcoming-events__empty">
                No hay eventos en este momento. Vuelve pronto para conocer las
                próximas actividades.
              </p>
            )}
          </div>
        </Container>
      </section>
      <section
        className="media-breaker"
        style={{
          backgroundImage: `url(${ImagenContacto.data.attributes.url})`,
          // backgroundImage: `url(${process.env.REACT_APP_API_URL}${ImagenContacto.data.attributes.url})`,
        }}
      ></section>
    </>
  );
}

export default Home;
